import { sql } from 'drizzle-orm';
import { index, integer, real, sqliteTable, text, uniqueIndex } from 'drizzle-orm/sqlite-core';
import { users } from './accounts';
import { syncedColumns } from './common';

type Json = Record<string, unknown>;

/** Server-only. One row, id 'global', inserted by migration 0001_seed_app_settings. */
export const appSettings = sqliteTable('app_settings', {
  id: text('id').primaryKey(),
  inviteOnly: integer('invite_only', { mode: 'boolean' }).notNull().default(true),
  billingLive: integer('billing_live', { mode: 'boolean' }).notNull().default(false),
  updatedAt: text('updated_at'),
  updatedBy: text('updated_by').references(() => users.id),
});

/** Synced. The user's own module toggle. id = singletonId(userId, `module_states.${moduleId}`). */
export const moduleStates = sqliteTable(
  'module_states',
  {
    ...syncedColumns(),
    moduleId: text('module_id').notNull(),
    enabled: integer('enabled', { mode: 'boolean' }).notNull().default(false),
    plan: text('plan', { enum: ['free', 'paid'] })
      .notNull()
      .default('free'),
    settings: text('settings', { mode: 'json' })
      .$type<Json>()
      .notNull()
      .default(sql`'{}'`),
  },
  (t) => [uniqueIndex('module_states_user_module_uq').on(t.userId, t.moduleId)],
);

/** Server-only. Admin override per (user, module); no row means 'default'. */
export const moduleOverrides = sqliteTable(
  'module_overrides',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    moduleId: text('module_id').notNull(),
    mode: text('mode', { enum: ['default', 'forced_on', 'forced_off'] })
      .notNull()
      .default('default'),
    setBy: text('set_by')
      .notNull()
      .references(() => users.id),
    setAt: text('set_at').notNull(),
  },
  (t) => [uniqueIndex('module_overrides_user_module_uq').on(t.userId, t.moduleId)],
);

/** Server-only, append-only. Every admin action: who, which user, what, before/after, when. */
export const auditLog = sqliteTable(
  'audit_log',
  {
    id: text('id').primaryKey(),
    actorId: text('actor_id')
      .notNull()
      .references(() => users.id),
    targetUserId: text('target_user_id').references(() => users.id),
    /** e.g. 'module_override.set', 'invite.create', 'reset_link.create', 'app_settings.update' */
    action: text('action').notNull(),
    entityType: text('entity_type'),
    entityId: text('entity_id'),
    before: text('before', { mode: 'json' }).$type<Json>(),
    after: text('after', { mode: 'json' }).$type<Json>(),
    at: text('at').notNull(),
  },
  (t) => [
    index('audit_log_at_idx').on(t.at),
    index('audit_log_target_user_at_idx').on(t.targetUserId, t.at),
  ],
);

/**
 * Append-only change history (conventions §7). Exists on device and server; each side writes
 * its own rows while applying mutations, so it is never sent as a mutation itself.
 */
export const history = sqliteTable(
  'history',
  {
    id: text('id').primaryKey(),
    userId: text('user_id').notNull(),
    actorId: text('actor_id').notNull(),
    entityType: text('entity_type').notNull(),
    entityId: text('entity_id').notNull(),
    action: text('action', {
      enum: ['create', 'update', 'delete', 'restore', 'pay', 'confirm', 'skip', 'override'],
    }).notNull(),
    before: text('before', { mode: 'json' }).$type<Json>(),
    after: text('after', { mode: 'json' }).$type<Json>(),
    /** A ModuleId or 'system'. */
    sourceModule: text('source_module').notNull(),
    mutationId: text('mutation_id'),
    at: text('at').notNull(),
  },
  (t) => [
    index('history_user_entity_idx').on(t.userId, t.entityType, t.entityId),
    index('history_user_at_idx').on(t.userId, t.at),
    index('history_mutation_idx').on(t.mutationId),
  ],
);

/** Synced. Bento tile layout rows (conventions §8). */
export const tiles = sqliteTable(
  'tiles',
  {
    ...syncedColumns(),
    dashboardId: text('dashboard_id').notNull(),
    moduleId: text('module_id').notNull(),
    type: text('type').notNull(),
    position: real('position').notNull(),
    size: text('size', { enum: ['small', 'wide', 'tall'] })
      .notNull()
      .default('small'),
    hidden: integer('hidden', { mode: 'boolean' }).notNull().default(false),
    config: text('config', { mode: 'json' })
      .$type<Json>()
      .notNull()
      .default(sql`'{}'`),
  },
  (t) => [index('tiles_user_dashboard_idx').on(t.userId, t.dashboardId)],
);

/** Synced. One row per user, id = singletonId(userId, 'user_settings'). A missing row means defaults. */
export const userSettings = sqliteTable('user_settings', {
  ...syncedColumns(),
  /** 0 = Sunday, 1 = Monday. Every breakdown and week tile uses it. */
  weekStart: integer('week_start').notNull().default(1),
  /** Seed name -> ISO time it last ran, e.g. { spending: '2026-11-02T02:00:00.000Z' }. */
  seeds: text('seeds', { mode: 'json' })
    .$type<Record<string, string>>()
    .notNull()
    .default(sql`'{}'`),
});
